
import React, { ReactElement } from "react";
import Image from "next/image";
import {
  BlockTypes,
  StoryblokRichTextNode,
  StoryblokRichText
} from "@storyblok/react";
import { storyblokEditable } from "@storyblok/react/rsc";
import { TextStoryblok } from "@/component-types-sb";
import Title from "@/components/Title";

function getTextAlignClass(textAlign?: string) {
  switch (textAlign) {
    case "center":
      return "text-center";
    case "right":
      return "text-right";
    case "justify":
      return "text-justify";
    case "left":
      return "text-left";
    default:
      return undefined;
  }
}

function HeadingBlock(node: Readonly<StoryblokRichTextNode<ReactElement>>) {
  const key = node.attrs?._uid || node.attrs?.id || `title-${crypto.randomUUID()}`;
  const variant = node.attrs?.level ? `h${node.attrs["level"]}` : "p";
  const alignClass = getTextAlignClass(node.attrs?.textAlign);
  return (
    <Title
      key={key}
      variant={variant as "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p"}
      className={alignClass}
    >
      {node.children}
    </Title>
  );
}

function ParagraphBlock(node: Readonly<StoryblokRichTextNode<ReactElement>>) {
  const key = node.attrs?._uid || node.attrs?.id || `paragraph-${crypto.randomUUID()}`;
  const alignClass = getTextAlignClass(node.attrs?.textAlign);
  return (
    <p key={key} className={alignClass}>
      {node.children}
    </p>
  );
}

function QuoteBlock(node: Readonly<StoryblokRichTextNode<ReactElement>>) {
  const key = node.attrs?._uid || node.attrs?.id || `blockquote-${crypto.randomUUID()}`;
  const alignClass = getTextAlignClass(node.attrs?.textAlign);
  const baseClass = "my-6 border-l-4 border-primary pl-6 italic";
  const className = alignClass ? `${baseClass} ${alignClass}` : baseClass;
  return (
    <blockquote key={key} className={className}>
      {node.children}
    </blockquote>
  );
}

export default function RichText({
  blok,
}: Readonly<{ blok: TextStoryblok | undefined }>) {
  if (!blok?.content) {
    return null;
  }

  const resolvers = {
    /*[MarkTypes.LINK]: (node: StoryblokRichTextNode<ReactElement>) => {
      return node.attrs?.linktype === 'story'
         ? (
            <Link
              href={node.attrs?.href}
              target={node.attrs?.target}
            >
              {node.text}
            </Link>
          )
        : (
            <a
              href={node.attrs?.href}
              target={node.attrs?.target}
            >
              {node.text}
            </a>
          );
    },*/
    [BlockTypes.HEADING]: HeadingBlock,
    [BlockTypes.PARAGRAPH]: ParagraphBlock,
    [BlockTypes.QUOTE]: QuoteBlock,
  };

  const renderRichText = (
    <StoryblokRichText
      // @ts-expect-error: Ignore type incompatibility between RichtextStoryblok and StoryblokRichTextNode
      doc={blok.content}
      resolvers={resolvers}
    />
  );

  if (blok.image?.filename) {
    const size = blok.image_size || "m";
    const alignment = blok.image_alignment || "right";
    let widthClass = "w-1/2 md:w-1/3 max-w-1/2 md:max-w-1/3";
    if (size === "s") {
      widthClass = "w-1/2 md:w-1/4 max-w-1/2 md:max-w-1/4";
    }
    if (size === "l") {
      widthClass = "w-1/2 max-w-1/2";
    }
    const floatClasses = alignment === "left" ? "float-left mb-3 mr-6 ml-0" : "float-right mb-4 ml-6 md:ml-10 mr-0";
    const clearClasses = alignment === "left" ? "clear-left" : "clear-right";

    return (
      <div {...storyblokEditable(blok)}>
        <Image
          src={`${blok.image.filename}`}
          alt={blok.image.title || 'Image'}
          width={500}
          height={300}
          className={`${floatClasses} object-cover rounded-lg ${widthClass} ${clearClasses}`}
        />
        {renderRichText}
      </div>
    );
  }

  return <div {...storyblokEditable(blok)}>{renderRichText}</div>;
}
